import { Flex } from "antd"
import React, { useEffect } from "react"

import { FixedContainer } from "../../components/FixedContainer.tsx"
import { homeColor, homeSize } from "./home-theme.ts"
import { generateDocumentTitle } from "../../constants/document-title.ts"
import { useAppSelector } from "../../hooks/use-app-selector.ts"
import { BranchSetupFlow } from "../Branches/components/BranchSetupFlow.tsx"
import { CompanyDashboardPage } from "../Branches/pages/CompanyDashboardPage.tsx"
import {
  selectActiveBranchId,
  selectIsCompanyMode,
  selectIsOwner
} from "../Branches/redux/branches.slice.ts"
import { HomeFavoriteReports } from "../ReportsPage/components/home/HomeFavoriteReports.tsx"
import {
  HomeGreeting,
  HomeNotificationsPanel,
  HomeRecentList,
  HomeShortcuts
} from "./components/HomeSections.tsx"
import { MobileHome } from "./components/MobileHome"
import { WidgetsDashboard } from "./components/Portfolio/WidgetsDashboard.tsx"

// A single operational branch's own scope. `WidgetsDashboard` treats an
// empty/undefined `teamScope` as "every branch the caller can see" (the
// Company HQ no-filter case) — so a branch workspace MUST pass its own id
// here, or every tile silently falls back to the whole company's data,
// identically, whichever branch is selected.
const useHomeTeamScope = () => {
  const activeBranchId = useAppSelector(selectActiveBranchId)
  return activeBranchId ? [activeBranchId] : []
}

/**
 * Home's own canvas.
 *
 * It bleeds out over `#main-content`'s padding and lays its own 24px gutter
 * instead, so the shell accounts for the sidebar exactly once and Home starts
 * a fixed distance from it however wide (or collapsed) that rail is. No
 * max-width and no `mx-auto`: those were what left a blank column down the
 * left on a wide monitor. No `overflow-x-clip` either — overflow is fixed at
 * the source (`min-w-0` on every grid/flex child) rather than hidden.
 *
 * The bleed is `FixedContainer bleedInline`'s, not this element's. That column
 * IS the page's scroll box, so a canvas that cancels `#main-content`'s padding
 * from inside it overshoots the scroller by the width of its `p-1 -m-1` ring —
 * 12px of real horizontal scrollbar at every width. Moving the bleed up to the
 * scroll box itself lets this fill it exactly.
 */
const HomeCanvas = ({ children }: React.PropsWithChildren) => (
  <div
    className={"home-canvas min-h-full"}
    style={{
      background: homeColor.canvas,
      boxSizing: "border-box",
      paddingInline: homeSize.contentGutter,
      paddingTop: homeSize.contentGutter - 4,
      paddingBottom: homeSize.contentGutter + 8
    }}
  >
    <Flex vertical gap={homeSize.sectionGap} className={"w-full min-w-0"}>
      {children}
    </Flex>
  </div>
)

const WidgetsHome = () => {
  const teamScope = useHomeTeamScope()

  useEffect(() => {
    generateDocumentTitle("Home")
  }, [])

  return (
    <FixedContainer bleedInline>
      {/* `hidden` would make this a second scroller, stranding the column's bottom reserve as a blank band */}
      <HomeCanvas>
        <BranchSetupFlow />
        {/* The two-column body is `WidgetsDashboard`'s own grid, so the
            sections below the fold are handed to it rather than starting
            their own band under it: the shortcut row and the recent list
            continue the main column, Notifications rides the right rail.

            They deliberately do NOT run full width under the rail. A busy
            account's Next Best Actions makes the rail roughly 880px taller
            than the main column, and a full-width band can only start below
            the taller of the two — which is exactly the reserved empty space
            this pass set out to remove. */}
        <WidgetsDashboard
          teamScope={teamScope}
          renderHeader={(viewControls, exportControl) => (
            <HomeGreeting
              viewControls={viewControls}
              exportControl={exportControl}
            />
          )}
          sideColumnFooter={<HomeNotificationsPanel />}
          mainColumnFooter={
            <>
              <HomeShortcuts />
              <HomeFavoriteReports />
              <HomeRecentList />
            </>
          }
        />
      </HomeCanvas>
    </FixedContainer>
  )
}

const MobileWidgetsHome = () => {
  useEffect(() => {
    generateDocumentTitle("Home")
  }, [])

  return (
    <FixedContainer>
      <div className={"overflow-x-clip"}>
        <BranchSetupFlow />
        <MobileHome />
      </div>
    </FixedContainer>
  )
}

const HomePage = () => {
  const isMobile = useAppSelector((state) => state.nav.isMobile)
  const isCompanyMode = useAppSelector(selectIsCompanyMode)
  const isOwner = useAppSelector(selectIsOwner)

  // The company overview replaces this page's content entirely while a
  // multi-branch OWNER is looking at the whole company — the single-branch
  // operational Home (with its project-creation/search/filter toolbar)
  // stays exactly as it was for every other case, mobile included. `isOwner`
  // was missing here: `globalBranch` defaults to `ALL_BRANCHES` before
  // `fetchAccessibleBranches` resolves, so on every fresh login/reload ANY
  // non-owner briefly read as company mode and this mounted
  // `CompanyDashboardPage`, whose effect unconditionally calls the
  // owner-gated `company_dashboard` endpoint — the "Only the GCC Owner can
  // manage branches" toast firing for ordinary staff. Having access to
  // multiple branches (a non-owner multi-branch employee) must never grant
  // HQ access either, race or no race.
  if (isCompanyMode && isOwner) return <CompanyDashboardPage />

  return isMobile ? <MobileWidgetsHome /> : <WidgetsHome />
}

export default HomePage
